import React from 'react'

import { isWithinInterval } from 'date-fns'
import { Controller, useForm } from 'react-hook-form'

import { CustomButton } from '@components/CustomButton'
import { FormDatePicker } from '@components/Form/FormDatePicker'
import { FormInput } from '@components/Form/FormInput'
import { FormMultiselect } from '@components/Form/FormMultiselect'
import { FormSelect } from '@components/Form/FormSelect'
import { FormHeader } from '@containers/Headers/FormHeader'
import { useFindSiteAssignees } from '@hooks/catalogs/assignees'
import { useFindSiteResources } from '@hooks/catalogs/resources'
import { useDate } from '@hooks/dates'
import { useUtils } from '@hooks/utils'
import { IGenericAssignment } from '@interfaces/task/generic/IGenericAssignment'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'

import { TAssignmentForm, useAssignmentsForm } from './schema'
import { ButtonContainer } from './styles'

type TTask = Pick<IGenericTask, 'code' | 'startTime' | 'duration'>

interface IProps {
  onDelete?: () => void
  onSave: (data: TAssignmentForm) => void
  onClose: () => void
  title: string
  task: TTask
  assignment?: Partial<IGenericAssignment>
}

export function AssignmentForm({
  onDelete,
  onSave,
  onClose,
  title,
  task,
  assignment,
}: IProps) {
  const { sortParams } = useUtils()

  const { data: rawResources } = useFindSiteResources()
  const { data: rawAssignees } = useFindSiteAssignees()

  const { getResolvedAssignmentsSchema } = useAssignmentsForm()

  const resources =
    rawResources?.sort((a, b) => sortParams(a.description, b.description)) ?? []
  const assignees =
    rawAssignees?.sort((a, b) => sortParams(a.description, b.description)) ?? []

  const { getWeekRange } = useDate()
  const { weekStart, weekEnd } = getWeekRange(new Date())
  const isNotBetweenCurrentWeek = !isWithinInterval(new Date(task?.startTime), {
    start: weekStart,
    end: weekEnd,
  })
  const hasCode = !!task.code

  const defaultValues = {
    resourceId: assignment?.resourceId,
    crewSize: assignment?.crewSize,
    duration: assignment?.duration,
    assigneeIds: assignment?.assigneeIds,
    startTime: task?.startTime && new Date(task.startTime),
  }

  const {
    control,
    formState: { isDirty },
    watch,
    handleSubmit,
  } = useForm<TAssignmentForm>({
    resolver: getResolvedAssignmentsSchema(task.duration),
    defaultValues,
  })

  const formValues = watch()

  const filteredAssignees = assignees.filter((assignee) => {
    return assignee.resourceIds.includes(Number(formValues.resourceId))
  })

  const assigneesFieldPlaceholder =
    filteredAssignees.length === 0
      ? 'No assignees available for selection'
      : 'No assignees selected'

  const resourceList = resources.map((item) => {
    return {
      label: item.description,
      value: item.id,
    }
  })

  return (
    <FormHeader
      onPressLeft={onClose}
      isRightButtonDisabled={!isDirty}
      leftLabel="Close"
      rightLabel="Save"
      onPressRight={handleSubmit(onSave)}
      title={title}
    >
      <div>
        <Controller
          control={control}
          name="resourceId"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormSelect
              mb={4}
              placeholder="Select a Resource"
              isDisabled={hasCode || isNotBetweenCurrentWeek}
              errorMessage={error?.message}
              isRequired
              label="Resource"
              items={resourceList}
              onValueChange={(value) => onChange(value)}
              selectedValue={value?.toString()}
            />
          )}
        />

        <Controller
          control={control}
          name="crewSize"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormInput
              isDisabled={hasCode || isNotBetweenCurrentWeek}
              errorMessage={error?.message}
              isRequired
              label="Crew"
              placeholder="Add Crew Size"
              onChange={(e) => onChange(e.target.value)}
              value={value?.toString()}
            />
          )}
        />

        <Controller
          control={control}
          name="duration"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormInput
              isDisabled={hasCode || isNotBetweenCurrentWeek}
              isRequired
              errorMessage={error?.message}
              label="Duration (hours)"
              placeholder="Add Resource Duration"
              onChange={(e) => onChange(e.target.value)}
              value={value?.toString()}
            />
          )}
        />

        <Controller
          control={control}
          name="startTime"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormDatePicker
              isDisabled={
                hasCode || (isNotBetweenCurrentWeek && !!task.startTime)
              }
              label="Start Time"
              isRequired
              onChange={onChange}
              value={value}
              mode="time"
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="assigneeIds"
          render={({
            field: { onChange },
            fieldState: { error },
            formState: { defaultValues },
          }) => (
            <FormMultiselect
              placeHolder={assigneesFieldPlaceholder}
              isDisabled={
                isNotBetweenCurrentWeek || filteredAssignees.length === 0
              }
              errorMessage={error?.message}
              onChange={onChange}
              items={filteredAssignees}
              defaultValues={defaultValues?.assigneeIds}
              label="Assignees"
            />
          )}
        />
      </div>
      {onDelete && !hasCode && (
        <ButtonContainer>
          <CustomButton
            isDisabled={hasCode || isNotBetweenCurrentWeek}
            onPress={onDelete}
          >
            Delete Resource
          </CustomButton>
        </ButtonContainer>
      )}
    </FormHeader>
  )
}
